import React, { useMemo } from 'react';
import {
  AnalysisModel, MIX_KINDS, MIX_LABEL, MissingData, MixKind, PlayerModel, RoundFilter, ScoringRow,
  fmtToPar, isCompetitor, scoringMix,
} from '../../utils/majorAnalysis';
import { MIX_COLORS } from '../../utils/chartPalette';
import { HolesAsk, ParsAsk } from './DataNeeded';
import { Empty, PlayerCell, RankCell, SortTh, fmtPct, sortRows, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  players: PlayerModel[];
  filter: RoundFilter;
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
}

export const MixBar: React.FC<{ counts: Record<MixKind, number>; total: number; label: string }> = ({ counts, total, label }) => (
  <div className="ma-mix" role="img" aria-label={`${label}: ${MIX_KINDS.filter((k) => counts[k]).map((k) => `${counts[k]} ${MIX_LABEL[k]}`).join(', ')}`}>
    {MIX_KINDS.filter((k) => counts[k] > 0).map((k) => (
      <span key={k} style={{ width: `${(counts[k] / total) * 100}%`, background: MIX_COLORS[k] }} title={`${MIX_LABEL[k]}: ${counts[k]} (${fmtPct(counts[k], total)})`} />
    ))}
  </div>
);

const ScoringMix: React.FC<Props> = ({ model, players, filter, missing, onOpenRound }) => {
  const rows = useMemo(() => scoringMix(model, filter), [model, filter]);
  const { sort, toggle } = useSort();
  const byId = new Map(rows.map((r) => [r.player_id, r]));
  const holes = rows.reduce((n, r) => n + r.holes, 0);
  const noPar = rows.reduce((n, r) => n + r.noPar, 0);

  if (holes === 0) {
    return (
      <div className="ma-tab">
        {noPar > 0 ? (
          <Empty title="Birdies, pars and bogeys need the hole pars for these courses.">
            <ParsAsk missing={missing} />
          </Empty>
        ) : (
          <Empty title="Birdies, pars and bogeys need hole-by-hole scores — none are entered for this major yet.">
            <HolesAsk matches={missing.holeScores} onOpenRound={onOpenRound} unlocks="the scoring mix for every player" />
            <ParsAsk missing={missing} />
          </Empty>
        )}
      </div>
    );
  }

  const field = rows.filter((r) => model.players.find((p) => p.player_id === r.player_id && isCompetitor(p)));
  const fieldCounts = MIX_KINDS.reduce((acc, k) => ({ ...acc, [k]: field.reduce((n, r) => n + r.counts[k], 0) }), {} as Record<MixKind, number>);
  const fieldHoles = field.reduce((n, r) => n + r.holes, 0);
  const fieldByPar = (par: 3 | 4 | 5) => {
    const list = field.map((r) => r.byPar[par]).filter((x): x is { toPar: number; n: number } => !!x);
    const n = list.reduce((a, x) => a + x.n, 0);
    return n ? list.reduce((a, x) => a + x.toPar * x.n, 0) / n : null;
  };

  const val = (r: ScoringRow | undefined, key: string): number | null => {
    if (!r || !r.holes) return null;
    if ((MIX_KINDS as string[]).includes(key)) return r.counts[key as MixKind];
    switch (key) {
      case 'holes': return r.holes;
      case 'pob': return r.parOrBetter / r.holes;
      case 'p3': return r.byPar[3]?.toPar ?? null;
      case 'p4': return r.byPar[4]?.toPar ?? null;
      case 'p5': return r.byPar[5]?.toPar ?? null;
      case 'bb': return r.bounceBack.chances ? r.bounceBack.made / r.bounceBack.chances : null;
      case 'streak': return r.longestStreak;
      default: return null;
    }
  };
  // Bigger is better for these; lower (vs par, bad scores) for the rest.
  const firstDir = (key: string): 1 | -1 => (['eagle', 'birdie', 'par', 'pob', 'bb', 'streak', 'holes'].includes(key) ? -1 : 1);
  const sorted = sortRows(players, sort, (p) => p.order, (p, key) => val(byId.get(p.player_id), key));

  return (
    <div className="ma-tab">
      <p className="ma-lede">
        Gross scores against each hole’s par, over {holes} hole{holes === 1 ? '' : 's'}.
        {noPar > 0 && ` ${noPar} hole${noPar === 1 ? '' : 's'} played on tee boxes without pars are left out.`}
      </p>
      <div className="ma-legend ma-legend--static" aria-hidden>
        {MIX_KINDS.map((k) => <span key={k} className="ma-legend-item"><span className="ma-swatch ma-swatch--sq" style={{ background: MIX_COLORS[k] }} />{MIX_LABEL[k]}</span>)}
      </div>
      <div className="ma-scroll">
        <table className="tbl tbl--sm ma-table ma-table--sticky">
          <thead>
            <tr>
              <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
              <th className="ma-stick-2">Player</th>
              <SortTh label="Holes" sortKey="holes" sort={sort} onSort={toggle} firstDir={-1} className="c" />
              <th className="ma-mix-col">Mix</th>
              {MIX_KINDS.map((k) => <SortTh key={k} label={MIX_LABEL[k]} sortKey={k} sort={sort} onSort={toggle} firstDir={firstDir(k)} className="c" />)}
              <SortTh label="Par or better" sortKey="pob" sort={sort} onSort={toggle} firstDir={-1} className="c" />
              <SortTh label="Par 3s" sortKey="p3" sort={sort} onSort={toggle} className="c" title="Average score vs par on par 3s" />
              <SortTh label="Par 4s" sortKey="p4" sort={sort} onSort={toggle} className="c" title="Average score vs par on par 4s" />
              <SortTh label="Par 5s" sortKey="p5" sort={sort} onSort={toggle} className="c" title="Average score vs par on par 5s" />
              <SortTh label="Bounce-back" sortKey="bb" sort={sort} onSort={toggle} firstDir={-1} className="c" title="Par or better on the hole after a bogey or worse" />
              <SortTh label="Best run" sortKey="streak" sort={sort} onSort={toggle} firstDir={-1} className="c" title="Longest run of holes at par or better" />
            </tr>
          </thead>
          <tbody>
            <tr className="ma-field-row">
              <td className="c ma-stick-1">—</td>
              <td className="ma-name-td ma-stick-2">Field</td>
              <td className="c">{fieldHoles}</td>
              <td className="ma-mix-col">{fieldHoles > 0 && <MixBar counts={fieldCounts} total={fieldHoles} label="Field" />}</td>
              {MIX_KINDS.map((k) => <td key={k} className="c">{fieldCounts[k]}</td>)}
              <td className="c">{fmtPct(fieldCounts.eagle + fieldCounts.birdie + fieldCounts.par, fieldHoles)}</td>
              <td className="c">{fmtToPar(fieldByPar(3), 2)}</td>
              <td className="c">{fmtToPar(fieldByPar(4), 2)}</td>
              <td className="c">{fmtToPar(fieldByPar(5), 2)}</td>
              <td className="c" colSpan={2} />
            </tr>
            {sorted.map((p) => {
              const r = byId.get(p.player_id);
              const has = !!r && r.holes > 0;
              return (
                <tr key={p.player_id}>
                  <RankCell p={p} all={model.players} />
                  <PlayerCell p={p} />
                  <td className="c">{has ? r!.holes : '—'}</td>
                  <td className="ma-mix-col">{has && <MixBar counts={r!.counts} total={r!.holes} label={p.name} />}</td>
                  {MIX_KINDS.map((k) => <td key={k} className="c">{has ? r!.counts[k] : '—'}</td>)}
                  <td className="c ma-strong">{has ? fmtPct(r!.parOrBetter, r!.holes) : '—'}</td>
                  <td className="c">{has ? fmtToPar(r!.byPar[3]?.toPar, 2) : '—'}</td>
                  <td className="c">{has ? fmtToPar(r!.byPar[4]?.toPar, 2) : '—'}</td>
                  <td className="c">{has ? fmtToPar(r!.byPar[5]?.toPar, 2) : '—'}</td>
                  <td className="c">{has ? fmtPct(r!.bounceBack.made, r!.bounceBack.chances) : '—'}</td>
                  <td className="c">{has ? r!.longestStreak : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(missing.holeScores.length > 0 || missing.teeboxesWithoutPars.length > 0) && (
        <>
          <p className="ma-foot">Based on {holes} holes — some rounds are still missing data:</p>
          <HolesAsk matches={missing.holeScores} onOpenRound={onOpenRound} />
          <ParsAsk missing={missing} />
        </>
      )}
    </div>
  );
};

export default ScoringMix;
